/**
 * TabBar — Chat / Spine tab row, Variant C chat-first redesign.
 *
 * Spec 29 Phase 1. Replaces the retired ``SpineTimelineToggle``.
 * Sits between TopBar + ContextStrip above and the tab body below.
 *
 * SRP: pure presentation. Receives activeTab + showSpine + callback;
 * emits tab-change intent. No state, no side effects.
 *
 * # DECISION: the Spine tab is hidden entirely when ``showSpine`` is
 * #   false, not disabled with a tooltip.
 * # WHY: writer feedback (2026-04-23) — "chat interface central;
 * #      spine in a second tab whenever a task involves a drafting
 * #      sequence." A disabled tab is still visual chrome; a hidden
 * #      tab matches the "VERY CLEAN and NON-CLUNKY" bar. When no
 * #      run is in flight the writer sees only the chat tab (no
 * #      tab row at all — see renderTabRow below).
 * # DATE: 2026-04-23
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { useCallback, useEffect, useRef } from "react";
import { brandColors, fonts, inkColors, statusColors, surfaceColors } from "../tokens";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TabId = "chat" | "spine" | "issues" | "drafts" | "images";

export interface TabBarProps {
  /** Currently-active tab. */
  activeTab: TabId;
  /** Intent callback fired when the writer clicks a tab or uses the
   *  keyboard (arrow keys / Enter / Space). */
  onTabChange: (tab: TabId) => void;
  /** When false, the Spine tab is hidden entirely AND the whole tab
   *  row collapses to zero height (the caller can skip rendering the
   *  component, but hiding it here keeps the parent layout simpler).
   *  When true, both tabs render. */
  showSpine: boolean;
  /** When true, render the Issues tab beside Spine — the submission-wide list
   *  of every gap / regulatory issue across the run. Shown whenever the Spine
   *  is (a run exists / sections present). */
  showIssues?: boolean;
  /** Optional — compact counter chip on the Issues tab showing the number of
   *  open issues across the submission. Null hides the chip. */
  issuesCounter?: string | null;
  /** When true, render the Drafts tab beside Chat / Spine. The
   *  Drafts tab is run-agnostic — it lists every prior completed
   *  run for the current scope (doc / workspace / library) — so the
   *  caller may show it independently of ``showSpine``. */
  showDrafts?: boolean;
  /** When true, render the Images tab — the image-search view (browse +
   *  insert the library's figures). Run-agnostic, like Drafts; shown
   *  whenever a library is bound. */
  showImages?: boolean;
  /** Optional — compact counter chip on the Spine tab for at-a-glance
   *  progress ("5/38") while a run is in flight. Null hides the chip. */
  spineCounter?: string | null;
  /** When true, the Spine tab renders a pulsing ``hot``-status dot
   *  next to its label so the writer sees that drafting is active
   *  without leaving the Chat tab. Caller drives this from the run
   *  lifecycle (typically ``state.state === "mid-run"``). The dot
   *  is purely visual progress signal; the ``spineCounter`` chip
   *  remains the source of truth for "how far". */
  spineActive?: boolean;
  /** Handler for the Stop/Reset button at the far right of the tab
   *  row. When provided the button renders; when omitted it is hidden.
   *  Caller passes ``handleStopRun`` (mid-run/approval) or
   *  ``handleReset`` (post-run) — same handlers previously wired to
   *  CommandRail's ``onReset`` prop. */
  onReset?: () => void;
  /** Label for the Stop/Reset button. Caller passes "stop" during
   *  mid-run/approval and "reset" post-run — identical to the
   *  ``resetLabel`` prop previously passed to CommandRail. */
  resetLabel?: string;
  /** Optional Save action — flushes pending debounced session patches
   *  and any write-back-bridge edit buffer so the writer has a single
   *  trustworthy "all my changes are committed" button. Only renders
   *  when provided. */
  onSave?: () => void | Promise<void>;
  /** When true, the Save button shows a "Saving…" busy label and is
   *  disabled to prevent double-clicks. */
  saveBusy?: boolean;
  /** Display label for the Save button. Caller swaps to "Saved" briefly
   *  after a successful flush. Defaults to "Save". */
  saveLabel?: string;
  /** When true, the Save button is disabled with ``saveDisabledTooltip``
   *  shown on hover. Used while a live draft run is in flight so the
   *  writer can't snapshot a half-built draft. */
  saveDisabled?: boolean;
  /** Hover tooltip shown when ``saveDisabled`` is true. */
  saveDisabledTooltip?: string;
  /** Non-null when the last Save failed (fetch failure or per-section
   *  errors) — renders the button in the error color with the message as
   *  its hover tooltip, so a failed Save can never read as "Saved". */
  saveError?: string | null;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexShrink: 0,
    background: surfaceColors.paper,
    borderBottom: `1px solid ${surfaceColors.line}`,
    padding: "0 12px",
    gap: "6px",
    fontFamily: fonts.body,
    // Horizontal scroll so additional buttons (Save, token counter,
    // stop/reset) can coexist on narrow add-in panels without truncating.
    // Scrollbar hidden — the writer scrolls via touchpad/wheel; visible
    // scrollbar chrome would clash with the underline-style active tab.
    overflowX: "auto",
    overflowY: "hidden",
    whiteSpace: "nowrap",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "::-webkit-scrollbar": {
      display: "none",
    },
    "@media (max-width: 520px)": {
      // Tabs carry 8px of their own padding: 4 + 8 puts the first tab's
      // text on the pane's 12px gutter.
      padding: "0 4px",
      gap: "4px",
    },
  },
  // Hidden variant — used by the caller via conditional render, but
  // also available here so the component can self-hide when neither
  // tab should appear (defensive, currently unused).
  hidden: {
    display: "none",
  },
  tab: {
    // Resembles a pill but integrated with the bottom border —
    // underline-style active state keeps vertical space tight.
    background: "transparent",
    border: "none",
    borderBottom: "2px solid transparent",
    color: inkColors.ink4,
    cursor: "pointer",
    fontFamily: fonts.mono,
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    padding: "10px 10px 8px",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    position: "relative",
    flexShrink: 0,
    // Click target meets WCAG minimum (40px tall) via padding.
    "&:hover": {
      color: inkColors.ink,
    },
    "&:focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "-2px",
      borderRadius: "2px",
    },
    "@media (max-width: 520px)": {
      fontSize: "10px",
      letterSpacing: "0.4px",
      padding: "9px 8px 7px",
      gap: "5px",
    },
  },
  tabActive: {
    color: inkColors.ink,
    borderBottom: `2px solid ${brandColors.primary}`,
  },
  // Small numeric chip rendered inside the Spine tab for live
  // "5/38" progress. Styled quietly so it reads as supporting meta,
  // not a distraction.
  counter: {
    // Launch-film badge: borderless violet tint, italic mono numeral.
    fontSize: "10px",
    fontWeight: 600,
    fontStyle: "italic",
    color: brandColors.primary,
    background: brandColors.light,
    border: "1px solid transparent",
    borderRadius: "5px",
    padding: "1px 6px",
    letterSpacing: "0.2px",
    // Resets any inherited uppercase from the tab label so "5/38"
    // renders as written.
    textTransform: "none",
    fontFamily: fonts.mono,
    fontVariantNumeric: "tabular-nums",
  },
  counterActive: {
    color: brandColors.primary,
    background: brandColors.light,
    // Use shorthand — base class declares ``border`` shorthand so
    // longhand override would conflict with Griffel's types.
    border: `1px solid ${brandColors.primary}55`,
  },
  // Phase 9.1 — drafting-active indicator on the Spine tab.
  // ``hot`` is the project's "active / running, drafting now" token
  // (see tokens.ts); reusing it keeps the colour vocabulary stable
  // across the spine pulse, the active-row dot, and this tab dot.
  // The pulse mirrors the timing of the streaming-pulse style in
  // TimelineView (1200ms ease-in-out) so simultaneous indicators
  // feel synchronised rather than competing.
  activeDot: {
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    background: statusColors.hot,
    flexShrink: 0,
    animationName: {
      "0%": { opacity: 0.35 },
      "50%": { opacity: 1 },
      "100%": { opacity: 0.35 },
    },
    animationDuration: "1200ms",
    animationIterationCount: "infinite",
    animationTimingFunction: "ease-in-out",
    "@media (prefers-reduced-motion: reduce)": {
      animation: "none",
      opacity: 1,
    },
  },
  // Stop/Reset button — floats to the right end of the tab row via
  // marginLeft:auto. Matches tab typography so it reads as part of
  // the same chrome. Colour vocabulary: hover → statusColors.hot,
  // the same orange as the pulsing Spine dot ("orange = live run").
  stopButton: {
    background: "transparent",
    border: "none",
    borderBottom: "2px solid transparent",
    color: inkColors.ink4,
    cursor: "pointer",
    fontFamily: fonts.mono,
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    padding: "10px 10px 8px",
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
    "&:hover": {
      color: statusColors.hot,
    },
    "&:focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "-2px",
      borderRadius: "2px",
    },
    "@media (max-width: 520px)": {
      fontSize: "10px",
      padding: "9px 8px 7px",
    },
  },
  // Save button — flushes pending edits. Lives in the same row as
  // Chat/Spine/Drafts and the Stop button. Hover uses ``brandColors``
  // (primary action) rather than ``statusColors.hot`` (destructive)
  // because Save is non-destructive.
  saveButton: {
    background: "transparent",
    border: "none",
    borderBottom: "2px solid transparent",
    color: inkColors.ink4,
    cursor: "pointer",
    fontFamily: fonts.mono,
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    padding: "10px 10px 8px",
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
    "&:hover": {
      color: brandColors.primary,
    },
    "&:disabled": {
      color: inkColors.ink4,
      cursor: "default",
      opacity: 0.6,
    },
    "&:focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "-2px",
      borderRadius: "2px",
    },
    "@media (max-width: 520px)": {
      fontSize: "10px",
      padding: "9px 8px 7px",
    },
  },
  // Save button in its failed state — hot color so a failed flush is
  // visually distinct from the idle/"Saved" states.
  saveButtonError: {
    color: statusColors.hot,
    "&:hover": {
      color: statusColors.hot,
    },
  },
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const TabBar: React.FC<TabBarProps> = ({
  activeTab,
  onTabChange,
  showSpine,
  showIssues = false,
  issuesCounter = null,
  showDrafts = false,
  showImages = false,
  spineCounter = null,
  spineActive = false,
  onReset,
  resetLabel,
  onSave,
  saveBusy = false,
  saveLabel,
  saveDisabled = false,
  saveDisabledTooltip,
  saveError = null,
}) => {
  const styles = useStyles();
  // ARIA: the active tab's button gets aria-selected; keyboard
  // navigation (Left/Right arrows) cycles through visible tabs. Keep
  // a ref to the container so we can focus a neighbouring tab button
  // on key press without reaching outside the component.
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      // Build the ordered list of visible tabs so arrow keys cycle
      // through whatever is actually rendered.
      const order: TabId[] = ["chat"];
      if (showSpine) order.push("spine");
      if (showIssues) order.push("issues");
      if (showDrafts) order.push("drafts");
      if (showImages) order.push("images");
      if (order.length < 2) return; // only one visible → nothing to navigate
      e.preventDefault();
      const idx = order.indexOf(activeTab);
      const delta = e.key === "ArrowRight" ? 1 : -1;
      const next = order[(idx + delta + order.length) % order.length];
      onTabChange(next);
    },
    [activeTab, onTabChange, showSpine, showIssues, showDrafts, showImages],
  );

  // Auto-focus the newly-active tab button so screen readers track
  // the selection change. Safe — this effect only fires on tab
  // swaps, not on every render.
  useEffect(() => {
    if (!containerRef.current) return;
    const selector = `[data-tab-id="${activeTab}"]`;
    const btn = containerRef.current.querySelector<HTMLButtonElement>(selector);
    // Only steal focus if focus was already inside the tab row (the
    // writer initiated the change via keyboard). Otherwise don't
    // hijack focus away from the chat input.
    const activeElement = document.activeElement;
    const focusInside = activeElement instanceof HTMLElement && containerRef.current.contains(activeElement);
    if (focusInside && btn) btn.focus();
  }, [activeTab]);

  return (
    <div ref={containerRef} className={styles.root} role="tablist" aria-label="View" onKeyDown={handleKeyDown}>
      <button
        type="button"
        role="tab"
        data-tab-id="chat"
        aria-selected={activeTab === "chat"}
        aria-controls="tab-panel-chat"
        tabIndex={activeTab === "chat" ? 0 : -1}
        className={mergeClasses(styles.tab, activeTab === "chat" && styles.tabActive)}
        onClick={() => onTabChange("chat")}
      >
        Chat
      </button>
      {showSpine && (
        <button
          type="button"
          role="tab"
          data-tab-id="spine"
          aria-selected={activeTab === "spine"}
          aria-controls="tab-panel-spine"
          tabIndex={activeTab === "spine" ? 0 : -1}
          className={mergeClasses(styles.tab, activeTab === "spine" && styles.tabActive)}
          onClick={() => onTabChange("spine")}
        >
          {spineActive && <span className={styles.activeDot} role="status" aria-label="Drafting in progress" />}
          Spine
          {spineCounter && (
            <span
              className={mergeClasses(styles.counter, activeTab === "spine" && styles.counterActive)}
              aria-label={`Progress: ${spineCounter}`}
            >
              {spineCounter}
            </span>
          )}
        </button>
      )}
      {showIssues && (
        <button
          type="button"
          role="tab"
          data-tab-id="issues"
          aria-selected={activeTab === "issues"}
          aria-controls="tab-panel-issues"
          tabIndex={activeTab === "issues" ? 0 : -1}
          className={mergeClasses(styles.tab, activeTab === "issues" && styles.tabActive)}
          onClick={() => onTabChange("issues")}
        >
          Issues
          {issuesCounter && (
            <span
              className={mergeClasses(styles.counter, activeTab === "issues" && styles.counterActive)}
              aria-label={`${issuesCounter} open issues`}
            >
              {issuesCounter}
            </span>
          )}
        </button>
      )}
      {showDrafts && (
        <button
          type="button"
          role="tab"
          data-tab-id="drafts"
          aria-selected={activeTab === "drafts"}
          aria-controls="tab-panel-drafts"
          tabIndex={activeTab === "drafts" ? 0 : -1}
          className={mergeClasses(styles.tab, activeTab === "drafts" && styles.tabActive)}
          onClick={() => onTabChange("drafts")}
        >
          Drafts
        </button>
      )}
      {showImages && (
        <button
          type="button"
          role="tab"
          data-tab-id="images"
          aria-selected={activeTab === "images"}
          aria-controls="tab-panel-images"
          tabIndex={activeTab === "images" ? 0 : -1}
          className={mergeClasses(styles.tab, activeTab === "images" && styles.tabActive)}
          onClick={() => onTabChange("images")}
        >
          Images
        </button>
      )}

      {onSave && (
        <button
          type="button"
          className={mergeClasses(styles.saveButton, Boolean(saveError) && styles.saveButtonError)}
          onClick={() => {
            void onSave();
          }}
          disabled={saveBusy || saveDisabled}
          style={{ marginLeft: "auto" }}
          title={
            saveDisabled
              ? (saveDisabledTooltip ?? "Save is unavailable right now")
              : (saveError ?? "Flush any pending edits to the backend")
          }
        >
          {saveBusy ? "Saving…" : (saveLabel ?? "Save")}
        </button>
      )}
      {onReset && resetLabel !== "reset" && (
        <button type="button" className={styles.stopButton} onClick={onReset}>
          {resetLabel}
        </button>
      )}
    </div>
  );
};

export default TabBar;
